import i18next from 'i18next';
import type {
  KnowledgeProposalType,
  KnowledgeRunStatus,
  KnowledgeSignal,
  KnowledgeUnitStatus,
} from '@mygpt/app/apis/services/knowledgeLoop';

export const CARD_BORDER = '1px solid #E9ECEF';
export const SIGNALS: KnowledgeSignal[] = ['ticket', 'edit_diff', 'unanswered', 'negative_feedback'];
export const REJECT_CODES = ['not_generalizable', 'inaccurate', 'duplicate', 'outdated', 'pii', 'other'] as const;

export const proposalColor = (p: KnowledgeProposalType): string =>
  ({ add: 'teal', update: 'blue', conflict: 'orange' }[p] || 'gray');

export const unitStatusColor = (s: KnowledgeUnitStatus): string =>
  ({
    candidate: 'yellow',
    approved: 'green',
    scheduled: 'teal',
    rejected: 'gray',
    merged: 'blue',
    superseded: 'gray',
    stale: 'orange',
  }[s] || 'gray');

export const runStatusColor = (s: KnowledgeRunStatus): string =>
  ({ running: 'blue', done: 'green', partial: 'yellow', error: 'red', empty: 'gray' }[s] || 'gray');

export const signalColor = (s: string): string =>
  ({ ticket: 'gray', edit_diff: 'violet', unanswered: 'orange', negative_feedback: 'red' }[s] || 'gray');

const pad = (n: number) => String(n).padStart(2, '0');

// 蒸留の時刻はすべて JST で見せる（実行時刻の設定が JST のため）
export const formatJst = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const jst = new Date(d.getTime() + 9 * 3600 * 1000);
  return `${jst.getUTCMonth() + 1}/${jst.getUTCDate()} ${pad(jst.getUTCHours())}:${pad(jst.getUTCMinutes())}`;
};

// 生効日は JST の日付で見せる（UTC の ISO を slice すると前日になる：2026-10-01 JST 0 時 = 2026-09-30T15:00Z）
export const formatJstDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const jst = new Date(d.getTime() + 9 * 3600 * 1000);
  return `${jst.getUTCFullYear()}-${pad(jst.getUTCMonth() + 1)}-${pad(jst.getUTCDate())}`;
};

export const formatDuration = (sec?: number | null): string => {
  if (sec === null || sec === undefined) return '—';
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  return m < 60 ? `${m}m ${Math.round(sec % 60)}s` : `${Math.floor(m / 60)}h ${m % 60}m`;
};

export const formatTokens = (n?: number | null): string => {
  if (!n) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
};

export const formatConfidence = (c?: number | null): string =>
  c === null || c === undefined ? '—' : `${Math.round(c * 100)}%`;

export const sumInputs = (inputs?: Partial<Record<KnowledgeSignal, number>>): number =>
  SIGNALS.reduce((acc, s) => acc + (inputs?.[s] || 0), 0);

/** PII の型別件数 → 「氏名 1・電話番号 2」 */
export const piiSummary = (counts?: Record<string, number> | null) =>
  Object.entries(counts || {})
    .filter(([, n]) => Number(n) > 0)
    .map(([k, n]) => `${i18next.t(`knowledge_loop.pii.type.${k}`, k)} ${n}`)
    .join('・');

const PII_FIELD_LABELS: Record<string, string> = {
  title: 'knowledge_loop.review.field_title',
  body: 'knowledge_loop.review.field_body',
  similar_questions: 'knowledge_loop.review.field_similar',
};

/**
 * 応答インターセプタが reject するのは axios の error ではなく ApiErrorResponse {statusCode, errorMsg, detail}。
 * 直接 axios を使った場合の error.response にも対応する
 */
export const errorStatus = (e: any): number | undefined => e?.statusCode ?? e?.response?.status;
export const errorPayload = (e: any): any =>
  e && typeof e === 'object' && 'detail' in e ? e.detail : e?.response?.data?.detail;

export const errorDetail = (e: any): string | undefined => {
  const detail = errorPayload(e);
  // 個人情報の準入（ルールは 400 'pii_detected'、単元は 409 {code, fields}）。値は返ってこない
  if (detail === 'pii_detected') return i18next.t('knowledge_loop.pii.rule_error');
  if (typeof detail === 'string' && detail.startsWith('rule must not contain numbers'))
    return i18next.t('zendesk_ops.rules.no_facts_note');
  if (detail && typeof detail === 'object' && detail.code === 'pii_detected') {
    const fields = Object.entries(detail.fields || {})
      .map(([f, counts]) => `${i18next.t(PII_FIELD_LABELS[f] || f)}：${piiSummary(counts as Record<string, number>)}`)
      .join(' / ');
    return i18next.t('knowledge_loop.pii.detected_error', { fields });
  }
  return typeof detail === 'string' ? detail : undefined;
};
